// ============================================
// Dispatcher Settings Page — Profile Management
// Wrapped in DispatcherLayout for sidebar/header
// ============================================

import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import api from '../../services/api';
import DispatcherLayout from '../../components/dispatcher/DispatcherLayout';
import { loadUser } from '../../redux/slices/authSlice';

const Settings = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const [formData, setFormData] = useState({
        name: user?.name || '',
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            await api.patch('/dispatcher/profile', formData);
            setMessage({ type: 'success', text: 'Profile updated successfully!' });
            dispatch(loadUser());
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'Update failed' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <DispatcherLayout title="Settings" subtitle="Manage your dispatcher profile">
            <main className="flex-1 overflow-y-auto p-4 sm:p-8 bg-transparent sidebar-scroll transition-colors duration-500">
                <div className="max-w-2xl mx-auto space-y-8">
                    <div className="relative bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 shadow-xl shadow-slate-200/50 dark:shadow-black/50 overflow-hidden group hover:shadow-2xl hover:shadow-emerald-500/20 dark:hover:shadow-emerald-500/10 transition-all duration-500">
                        <div className="p-8 sm:p-10 border-b border-white/60 dark:border-white/5 bg-white/40 dark:bg-slate-800/20 relative overflow-hidden transition-colors">
                            <div className="absolute -right-10 -top-10 w-32 h-32 bg-emerald-50 dark:bg-emerald-500/5 rounded-full opacity-40 group-hover:scale-150 transition-transform duration-700" />
                            <div className="flex flex-col sm:flex-row items-center gap-8 relative z-10">
                                <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-4xl font-black shadow-2xl shadow-emerald-500/30 transform group-hover:rotate-3 transition-transform">
                                    {user?.name?.charAt(0) || 'D'}
                                </div>
                                <div className="text-center sm:text-left">
                                    <h3 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">{user?.name}</h3>
                                    <p className="text-sm text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest">{user?.email}</p>
                                    <div className="mt-4 flex flex-wrap justify-center sm:justify-start gap-2">
                                        <span className="px-4 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px] font-black uppercase tracking-widest border border-emerald-100 dark:border-emerald-500/20 shadow-sm">
                                            Dispatcher Role
                                        </span>
                                        <span className="px-4 py-1.5 rounded-full bg-slate-900 dark:bg-white/10 text-white text-[10px] font-black uppercase tracking-widest shadow-lg shadow-black/10">
                                            Active Status
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="p-8 sm:p-10 space-y-8">
                            {message.text && (
                                <div className={`p-5 rounded-2xl text-xs font-black uppercase tracking-widest border ${message.type === 'success' ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-100 dark:border-emerald-500/20 text-emerald-700 dark:text-emerald-400 shadow-sm' : 'bg-red-50 dark:bg-red-500/10 border-red-100 dark:border-red-500/20 text-red-700 dark:text-red-400 shadow-sm'}`}>
                                    <div className="flex items-center gap-3">
                                        {message.type === 'success' ? (
                                            <svg className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                                        ) : (
                                            <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                                        )}
                                        {message.text}
                                    </div>
                                </div>
                            )}

                            <div className="grid gap-8">
                                <div className="space-y-3">
                                    <label className="ml-1 uppercase tracking-[0.2em] text-[10px] font-black text-slate-400 flex items-center gap-2">
                                        <span className="w-1 h-1 rounded-full bg-emerald-500" /> Display Name
                                    </label>
                                    <input
                                        name="name" type="text" value={formData.name} onChange={handleChange} required
                                        className="w-full px-6 py-4 rounded-2xl bg-white/80 dark:bg-slate-800/50 border-2 border-white/80 dark:border-white/10 focus:border-emerald-500 dark:focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/5 outline-none transition-all text-sm font-black text-slate-700 dark:text-white shadow-sm hover:border-slate-200 dark:hover:border-white/20"
                                    />
                                </div>

                                <div className="space-y-3">
                                    <label className="ml-1 uppercase tracking-[0.2em] text-[10px] font-black text-slate-400 flex items-center gap-2">
                                        <span className="w-1 h-1 rounded-full bg-slate-200" /> Email Address
                                    </label>
                                    <div className="relative group/input">
                                        <input
                                            value={user?.email} disabled
                                            className="w-full px-6 py-4 rounded-2xl bg-slate-50/80 dark:bg-slate-800/30 border-2 border-slate-100 dark:border-white/5 text-slate-400 dark:text-slate-500 cursor-not-allowed text-sm font-black shadow-inner"
                                        />
                                        <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover/input:opacity-100 transition-opacity">
                                            <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Locked</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <button type="submit" disabled={loading}
                                className="w-full py-5 rounded-2xl bg-slate-900 hover:bg-emerald-600 text-white text-xs font-black uppercase tracking-[0.2em] shadow-xl shadow-slate-900/10 hover:shadow-emerald-600/20 transition-all transform active:scale-[0.98] disabled:opacity-50"
                            >
                                {loading ? 'SYNCING DATA...' : 'UPDATE PROFILE →'}
                            </button>
                        </form>
                    </div>
                </div>
            </main>
        </DispatcherLayout>
    );
};

export default Settings;
